import { createPortal } from "react-dom";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "@/hooks/useAuth";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Set when this modal is opened from the invite-accept flow
   * (AcceptInvite). Presence of this prop — regardless of `email` — means:
   *   - skip personal-org creation on signup (the invitation-accept flow
   *     owns which org becomes active, via authClient.organization.setActive
   *     after acceptInvitation succeeds)
   *   - don't auto-navigate to /strategies after sign in/up; close the modal
   *     and let the parent page (AcceptInvite) drive its own accept + redirect
   * `email`, when present, prefills and locks the email field in both modes
   * so the credential the user authenticates with matches the invited
   * email BetterAuth enforces server-side on accept.
   */
  inviteContext?: { email?: string };
}

type AuthMode = "signin" | "signup";

export function AuthModal({ isOpen, onClose, inviteContext }: AuthModalProps) {
  const [mounted, setMounted] = useState(false);
  const [mode, setMode] = useState<AuthMode>("signin");
  const [name, setName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { signIn, signUp } = useAuth();
  const emailLocked = !!inviteContext?.email;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isOpen && inviteContext?.email) {
      setEmail(inviteContext.email);
    }
  }, [isOpen, inviteContext?.email]);

  const resetForm = useCallback(() => {
    setName("");
    setOrgName("");
    setEmail(inviteContext?.email ?? "");
    setPassword("");
    setError(null);
    setLoading(false);
  }, [inviteContext?.email]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (mode === "signin") {
        await signIn(email, password);
      } else {
        if (!name.trim()) {
          setError("Name is required");
          setLoading(false);
          return;
        }
        await signUp(email, password, name, {
          orgName: orgName.trim() || undefined,
          skipPersonalOrg: !!inviteContext,
        });
      }
      handleClose();
      // In invite-accept context, AcceptInvite owns the post-auth flow
      // (accept invitation, activate the invited org, then redirect).
      if (!inviteContext) {
        navigate("/strategies");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setMode((m) => (m === "signin" ? "signup" : "signin"));
    setError(null);
  };

  if (!mounted || !isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-8 shadow-xl">
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 text-slate-400 hover:text-slate-600"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M15 5L5 15M5 5l10 10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <h2 className="mb-6 text-xl font-bold text-slate-900">
          {mode === "signin" ? "Sign in to Sovrun" : "Create your account"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === "signup" && (
            <>
              <div>
                <label
                  htmlFor="auth-name"
                  className="mb-1 block text-sm font-medium text-slate-700"
                >
                  Name
                </label>
                <input
                  id="auth-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-1 focus:ring-slate-400"
                  placeholder="Your name"
                />
              </div>
              {!inviteContext && (
                <div>
                  <label
                    htmlFor="auth-org-name"
                    className="mb-1 block text-sm font-medium text-slate-700"
                  >
                    Organization name
                  </label>
                  <input
                    id="auth-org-name"
                    type="text"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-1 focus:ring-slate-400"
                    placeholder="Your company or team name"
                  />
                </div>
              )}
            </>
          )}

          <div>
            <label
              htmlFor="auth-email"
              className="mb-1 block text-sm font-medium text-slate-700"
            >
              Email
            </label>
            <input
              id="auth-email"
              type="email"
              value={email}
              onChange={(e) => !emailLocked && setEmail(e.target.value)}
              readOnly={emailLocked}
              className={`w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-1 focus:ring-slate-400 ${
                emailLocked ? "cursor-not-allowed bg-slate-50 text-slate-500" : ""
              }`}
              placeholder="you@example.com"
              required
            />
            {emailLocked && (
              <p className="mt-1 text-xs text-slate-400">
                This invitation was sent to this address.
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="auth-password"
              className="mb-1 block text-sm font-medium text-slate-700"
            >
              Password
            </label>
            <input
              id="auth-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-1 focus:ring-slate-400"
              placeholder={mode === "signup" ? "Min. 8 characters" : "Your password"}
              required
              minLength={mode === "signup" ? 8 : undefined}
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50"
          >
            {loading ? "Loading..." : mode === "signin" ? "Sign in" : "Create account"}
          </button>
        </form>

        <p className="mt-4 text-center text-sm text-slate-500">
          {mode === "signin" ? (
            <>
              Don&apos;t have an account?{" "}
              <button
                onClick={toggleMode}
                className="font-medium text-slate-900 hover:underline"
              >
                Sign up
              </button>
            </>
          ) : (
            <>
              Already have an account?{" "}
              <button
                onClick={toggleMode}
                className="font-medium text-slate-900 hover:underline"
              >
                Sign in
              </button>
            </>
          )}
        </p>
      </div>
    </div>,
    document.body
  );
}
